import { Pipe, PipeTransform } from "@angular/core";

interface Node {
  type: string; // text is just plant text, otherwise it's the name of a html element, ex: b, i, em, strong, etc
  value: string;
}

// Matches any opening html tag that has no attributes: <i>
const openingTagRegex = new RegExp(/<([a-z]+)>/gi);
// Matches any closing html tag that has no attributes: </i>
const closingTagRegex = new RegExp(/<\/([a-z]+)>/gi);

@Pipe({
  name: "boldSearch",
  standalone: true
})
export class BoldSearchPipe implements PipeTransform {
  transform(text: string, search: string | null): string {
    if (!search || search === "" || text === null) return text;

    const nodes = this.getNodes(text);

    // Removes any html tags so we can search between tags
    const plainText = this.getPlainTextFromNodes(nodes);
    const startingIndex = plainText.toLowerCase().indexOf(search.toLowerCase());

    // Search is not in the text
    if (startingIndex === -1) return text;

    const nodesBolded = this.boldNodes(nodes, search, startingIndex);
    const combinedHTML = this.getCombinedHTMLFromNodes(nodesBolded);

    return combinedHTML;
  }

  getNodes(html: string): Node[] {
    const nodes: Node[] = [];
    const stack: string[] = [];
    let currentValue = "";

    for (const letter of html) {
      currentValue = `${currentValue}${letter}`;

      const openingTagMatch = openingTagRegex.exec(currentValue);
      if (openingTagMatch?.length === 2) {
        const matchText = openingTagMatch[0];
        const tagType = openingTagMatch[1];
        const textWithoutOpeningTag = currentValue.replace(matchText, "");

        if (!currentValue.startsWith(matchText)) {
          nodes.push({ type: "text", value: textWithoutOpeningTag });
        }

        stack.push(tagType);
        currentValue = "";
      }

      const closingTagMatch = closingTagRegex.exec(currentValue);
      if (closingTagMatch?.length === 2) {
        const matchText = closingTagMatch[0];
        const textBeforeClosingTag = currentValue.replace(matchText, "");

        nodes.push({ type: stack[stack.length - 1] || "text", value: textBeforeClosingTag });
        stack.pop();
        currentValue = "";
      }
    }

    if (currentValue !== "") {
      nodes.push({ type: stack[stack.length - 1] || "text", value: currentValue });
    }

    return nodes;
  }

  boldNodes(nodes: Node[], search: string, startingIndex: number) {
    const endingIndex = startingIndex + search.length;
    let currentIndex = 0;

    return nodes.map((node) => {
      const text = node.value;
      const nextIndex = currentIndex + text.length;

      // The search text is not in this node
      if (endingIndex <= currentIndex || startingIndex >= nextIndex) {
        currentIndex = nextIndex;
        return node;
      }

      const start = Math.max(0, startingIndex - currentIndex);
      const end = Math.min(text.length, endingIndex - currentIndex);

      const beforeText = text.slice(0, start);
      const matchText = text.slice(start, end);
      const afterText = text.slice(end);

      node.value = `${beforeText}<strong><u>${matchText}</u></strong>${afterText}`;
      currentIndex = nextIndex;

      return node;
    });
  }

  getCombinedHTMLFromNodes(nodes: Node[]): string {
    return nodes.reduce((combinedHTML, currentNode) => {
      if (currentNode.type === "text") return combinedHTML + currentNode.value;

      return `${combinedHTML}<${currentNode.type}>${currentNode.value}</${currentNode.type}>`;
    }, "");
  }

  getPlainTextFromNodes(nodes: Node[]): string {
    return nodes.reduce((plainText, currentNode) => plainText + currentNode.value, "");
  }
}
